import { useMemo } from 'react';

/**
 * Normalizes a value for comparison: trim strings, treat empty/null/undefined/'N/A' as empty.
 * So "  VRT-120  " === "VRT-120", and "" | null | undefined | "N/A" are all treated as empty.
 * @param {*} value
 * @returns {string} Normalized string for comparison (empty string for "no value")
 */
function normalizeValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' || trimmed === 'N/A' ? '' : trimmed;
  }
  return String(value);
}

/**
 * Returns true if any of the given fields differ between initial and current (after normalization).
 * Reusable for any form: pass two objects and the list of keys to compare.
 * @param {Object} initialValues - Initial values (e.g. when dialog opened)
 * @param {Object} currentValues - Current form values
 * @param {string[]} [fields] - Keys to compare. If omitted, compares all keys present in initialValues.
 * @returns {boolean} True if at least one field has changed
 */
export function hasFormChanges(initialValues, currentValues, fields) {
  const keys =
    Array.isArray(fields) && fields.length > 0
      ? fields
      : Object.keys(initialValues || {});
  for (const key of keys) {
    const a = normalizeValue(initialValues?.[key]);
    const b = normalizeValue(currentValues?.[key]);
    if (a !== b) return true;
  }
  return false;
}

/**
 * Hook that returns whether the current form values have changed from initial values.
 * Use to enable/disable Save only when something actually changed (e.g. type then revert = no change).
 * @param {Object} initialValues - Initial values (e.g. { deskId, timeZone, role } when dialog opened)
 * @param {Object} currentValues - Current form state
 * @param {string[]} [fields] - Keys to compare. If omitted, compares all keys in initialValues.
 * @returns {{ hasChanges: boolean }}
 */
export function useFormChanges(initialValues, currentValues, fields) {
  const fieldsKey = Array.isArray(fields) ? fields.join(',') : '';
  return useMemo(
    () => ({
      hasChanges: hasFormChanges(initialValues, currentValues, fields),
    }),
    [initialValues, currentValues, fieldsKey]
  );
}

export default useFormChanges;
